import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    IconButton,
    Divider,
    Box,
    Button,
    Tooltip,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import FilterMonthYearPicker from '../../Widgets/CustomDates/FilterMonthYearPicker/FilterMonthYearPicker';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter';
import CardSelectionModal from '../CardSelectionModal/CardSelectionModal';
import { ForeignCurrencyCode, ForeignCurrencySymbol } from '../../../assests/Constants/constants';
import headerStyles from '../../ClientDetails/SubComponents/ClientDetailsHeader/HeaderToolbar.module.scss';
import '../../../pages/Document/Document.scss';
import './FilterBar.css';

const PAL_CODE_TYPE_SORT_ORDER = {
    'מאזני': 0,
    'מאזני רכוש קבוע': 1,
    'תוצאתי': 2,
};

const getFilterModeMenuProps = (direction = 'rtl') => ({
    anchorOrigin: {
        vertical: 'bottom',
        horizontal: direction === 'rtl' ? 'right' : 'left',
    },
    transformOrigin: {
        vertical: 'top',
        horizontal: direction === 'rtl' ? 'right' : 'left',
    },
    PaperProps: {
        sx: {
            mt: 0.5,
            direction,
            textAlign: direction === 'rtl' ? 'right' : 'left',
            borderRadius: '8px',
            border: '1px solid #e5e7f6',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            minWidth: 'var(--xtram-menu-width, 140px)',
            '& .MuiList-root': { p: 0, direction },
            '& .MuiMenuItem-root': {
                minHeight: 'var(--xtram-menu-item-height, 36px)',
                px: 'var(--xtram-menu-item-pad-x, 12px)',
                py: 'var(--xtram-menu-item-pad-y, 8px)',
                fontSize: 'var(--xtram-text-sm, 13px)',
                fontWeight: 400,
                lineHeight: 1.35,
                color: '#696665',
                justifyContent: direction === 'rtl' ? 'flex-end' : 'flex-start',
                transition: 'background-color 0.2s ease, color 0.2s ease',
                '&:not(:last-of-type)': { borderBottom: '1px solid #f0f0f0' },
                '&:hover': { backgroundColor: '#304fff', color: '#ffffff' },
                '&.Mui-selected': {
                    backgroundColor: 'rgba(48, 79, 255, 0.1)',
                    color: '#304fff',
                    '&:hover': { backgroundColor: '#304fff', color: '#ffffff' },
                },
            },
        },
    },
});

// numeric enum value -> currency code string (e.g. 0 -> "USD")
const currencyCodeToName = Object.fromEntries(
    Object.entries(ForeignCurrencyCode).map(([name, value]) => [value, name])
);

const FilterBar = ({
    searchQuery,
    setSearchQuery,
    startDate,
    endDate,
    isMonthly,
    handleStartDateChange,
    handleEndDateChange,
    handleStartDateChangeNew,
    handleEndDateChangeNew,
    handleRangeChange,
    processedFilesCheckbox,
    handleProcessFilesCheckboxClick,
    allAccountCards,
    selectedCards,
    setSelectedCards,
    cardSelectionMode,
    setCardSelectionMode,
    fromCard,
    setFromCard,
    toCard,
    setToCard,
    accountCardId,
    setAccountCardId,
    accountCardName,
    setAccountCardName,
    isMultiCardReport,
    setIsMultiCardReport,
    palCodeOptions,
    selectedPalCode,
    setSelectedPalCode,
    palCodeSelectionMode,
    setPalCodeSelectionMode,
    fromPalCode,
    setFromPalCode,
    toPalCode,
    setToPalCode,
    selectedPalCodeType,
    setSelectedPalCodeType,
    onApplySelection,
    direction,
    showZeros,
    setShowZeros,
    filterByDocumentDate,
    setFilterByDocumentDate,
    rowViewMode = 'full',
    setRowViewMode,
    availableCurrencies = [],
    selectedCurrency = null,
    setSelectedCurrency
}) => {
    const { t } = useTranslation();
    const [showCardSelectionModal, setShowCardSelectionModal] = useState(false);
    const autoSelectTriggeredRef = useRef(false);
    const prevPalCodeKeyRef = useRef('');

    // Currency options for the autocomplete: Shekel (default), Mixed, then available foreign currencies
    const currencyOptions = useMemo(() => {
        const options = [
            { value: 'SHEKEL', label: `${t('Shekel')} (₪)` },
            { value: 'MIXED', label: t('MixedCurrencies') },
        ];
        (availableCurrencies || []).forEach((code) => {
            const name = currencyCodeToName[code];
            const symbol = name ? ForeignCurrencySymbol[name] : '';
            options.push({ value: code, label: symbol ? `${name} (${symbol})` : name });
        });
        return options;
    }, [availableCurrencies, t]);

    // Map the current selectedCurrency value to the matching option object
    const selectedCurrencyOption = useMemo(() => {
        const key = selectedCurrency === null || selectedCurrency === undefined ? 'SHEKEL' : selectedCurrency;
        return currencyOptions.find((option) => option.value === key) || null;
    }, [currencyOptions, selectedCurrency]);

    const sortedPalCodeOptions = useMemo(() => {
        if (!palCodeOptions?.length) return [];

        return [...palCodeOptions].sort((a, b) => {
            const typeOrderA = PAL_CODE_TYPE_SORT_ORDER[a.palCodeType] ?? 999;
            const typeOrderB = PAL_CODE_TYPE_SORT_ORDER[b.palCodeType] ?? 999;
            if (typeOrderA !== typeOrderB) {
                return typeOrderA - typeOrderB;
            }

            const orderA = a.orderNumber ?? 9999;
            const orderB = b.orderNumber ?? 9999;
            return orderA - orderB;
        });
    }, [palCodeOptions]);

    const palCodeOrderMap = useMemo(() => {
        const map = {};
        sortedPalCodeOptions.forEach((pal, index) => {
            map[pal.value] = index;
        });
        return map;
    }, [sortedPalCodeOptions]);

    const groupPalCodeBy = (option) => option.palCodeType || t('Other');

    const palCodeTypeOptions = useMemo(
        () => ['מאזני', 'מאזני רכוש קבוע', 'תוצאתי'].map((type) => ({
            label: type,
            value: type,
        })),
        []
    );

    const palCodesForType = useMemo(() => {
        if (!selectedPalCodeType?.value) return [];
        return sortedPalCodeOptions
            .filter((pal) => pal.palCodeType === selectedPalCodeType.value)
            .map((pal) => pal.value);
    }, [selectedPalCodeType, sortedPalCodeOptions]);

    const sortAccountCardsByPalCodeSections = (cards) =>
        [...cards].sort((a, b) => {
            const palOrderA = palCodeOrderMap[a.palCode] ?? 9999;
            const palOrderB = palCodeOrderMap[b.palCode] ?? 9999;
            if (palOrderA !== palOrderB) {
                return palOrderA - palOrderB;
            }

            const aId = parseInt(a.cardId, 10);
            const bId = parseInt(b.cardId, 10);
            if (!Number.isNaN(aId) && !Number.isNaN(bId)) {
                return aId - bId;
            }
            return String(a.cardId).localeCompare(String(b.cardId));
        });

    // Filter cards by selected PAL code (single, range, or type)
    const filteredAccountCards = useMemo(() => {
        let filteredCards = [];
        
        if (palCodeSelectionMode === 'type' && selectedPalCodeType?.value) {
            filteredCards = allAccountCards.filter((card) => {
                if (!card.palCode || card.palCode.trim() === '') {
                    return palCodesForType.includes('NonPalCode');
                }
                return palCodesForType.includes(card.palCode);
            });
        } else if (palCodeSelectionMode === 'range' && fromPalCode && toPalCode) {
            // For range mode, filter cards that have PAL codes within the range
            const fromIndex = sortedPalCodeOptions.findIndex(pal => pal.value === fromPalCode.value);
            const toIndex = sortedPalCodeOptions.findIndex(pal => pal.value === toPalCode.value);
            
            if (fromIndex !== -1 && toIndex !== -1) {
                const start = Math.min(fromIndex, toIndex);
                const end = Math.max(fromIndex, toIndex);
                const rangePalCodes = sortedPalCodeOptions.slice(start, end + 1).map(pal => pal.value);
                
                filteredCards = allAccountCards.filter(card => {
                    if (!card.palCode || card.palCode.trim() === '') {
                        return rangePalCodes.includes('NonPalCode');
                    }
                    return rangePalCodes.includes(card.palCode);
                });
            } else {
                filteredCards = allAccountCards;
            }
        } else if (selectedPalCode && selectedPalCode.length > 0) {
            if (cardSelectionMode === 'multiple') {
                // For multiple mode, get all cards from first PAL code to selected PAL code
                const selectedPalCodeValue = selectedPalCode[0].value;
                const selectedIndex = sortedPalCodeOptions.findIndex(pal => pal.value === selectedPalCodeValue);
                
                if (selectedIndex !== -1) {
                    // Get all PAL codes from the first one to the selected one
                    const palCodesToInclude = sortedPalCodeOptions.slice(0, selectedIndex + 1).map(pal => pal.value);
                    filteredCards = allAccountCards.filter(card => 
                        palCodesToInclude.includes(card.palCode)
                    );
                } else {
                    filteredCards = allAccountCards;
                }
            } else {
                // For single mode, filter by selected PAL codes
                filteredCards = allAccountCards.filter(card => 
                    selectedPalCode.some(pal => pal.value === card.palCode)
                );
            }
        } else {
            filteredCards = allAccountCards;
        }
        
        return sortAccountCardsByPalCodeSections(filteredCards);
    }, [allAccountCards, selectedPalCode, fromPalCode, toPalCode, palCodeSelectionMode, sortedPalCodeOptions, palCodeOrderMap, selectedPalCodeType, palCodesForType]);

    const accountCardAutocompleteOptions = useMemo(
        () =>
            filteredAccountCards.map((card) => {
                const palOption = sortedPalCodeOptions.find((pal) => pal.value === card.palCode);
                return {
                    value: card.cardId,
                    label: `${card.cardId} - ${card.name}`,
                    group: palOption?.label || card.palCode || t('NonPalCode'),
                    card,
                };
            }),
        [filteredAccountCards, sortedPalCodeOptions, t]
    );

    const groupAccountCardBy = (option) => option.group;

    // Abstract function for handling both card and PAL code selections
    const handleSelection = (type, mode, shouldApply = true) => {
        if (type === 'card') {
            if (mode === 'single' && accountCardId) {
                setIsMultiCardReport(false);
            } else if (mode === 'range' && fromCard && toCard) {
                const fromIndex = filteredAccountCards.findIndex(card => card.cardId === fromCard.cardId);
                const toIndex = filteredAccountCards.findIndex(card => card.cardId === toCard.cardId);
                if (fromIndex !== -1 && toIndex !== -1) {
                    const start = Math.min(fromIndex, toIndex);
                    const end = Math.max(fromIndex, toIndex);
                    const rangeCards = filteredAccountCards.slice(start, end + 1);
                    setSelectedCards(rangeCards);
                    setIsMultiCardReport(true);

                } else {
                    // If indices not found, clear the selection
                    setSelectedCards([]);
                    setIsMultiCardReport(false);
                }
            } else if (mode === 'multiple' && selectedCards.length > 0) {
                setIsMultiCardReport(true);
            } else if (mode === 'all') {
                setSelectedCards(filteredAccountCards);
                setIsMultiCardReport(true);
            }
        } else if (type === 'palCode') {
            if (mode === 'single' && selectedPalCode && selectedPalCode.length > 0) {
                // PAL code single selection is already handled by the state
            } else if (mode === 'range' && fromPalCode && toPalCode) {
                const fromIndex = sortedPalCodeOptions.findIndex(pal => pal.value === fromPalCode.value);
                const toIndex = sortedPalCodeOptions.findIndex(pal => pal.value === toPalCode.value);
                if (fromIndex !== -1 && toIndex !== -1) {
                    const start = Math.min(fromIndex, toIndex);
                    const end = Math.max(fromIndex, toIndex);
                    const rangePalCodes = sortedPalCodeOptions.slice(start, end + 1);
                    setSelectedPalCode(rangePalCodes);
                }
            }
        }
        
        // Only apply once at the end if shouldApply is true
        if (shouldApply) {
            onApplySelection();
        }
    };

    const handleCardSelection = () => {
        // Check if PAL code is selected but no cards are explicitly selected
        const hasPalCodeSelection = 
            (palCodeSelectionMode === 'single' && selectedPalCode && selectedPalCode.length > 0) ||
            (palCodeSelectionMode === 'range' && fromPalCode && toPalCode) ||
            (palCodeSelectionMode === 'type' && selectedPalCodeType);
        
        // Check if no cards are actually selected (regardless of cardSelectionMode)
        const hasNoCardSelection = 
            cardSelectionMode !== 'all' && // 'all' means all cards, so skip auto-selection
            (!selectedCards || selectedCards.length === 0) &&
            !accountCardId &&
            (!fromCard || !toCard); // No range selection
        
        // Determine the effective card selection mode (may change if we auto-select cards)
        let effectiveCardSelectionMode = cardSelectionMode;
        let shouldAutoSelectCards = false;
        
        // If PAL code is selected but no cards are selected, automatically select all cards matching the PAL code
        // This mimics the "Select All" behavior in CardSelectionModal when PAL code is filtered
        if (hasPalCodeSelection && hasNoCardSelection && filteredAccountCards.length > 0) {
            // Automatically select all cards that match the selected PAL code(s)
            // filteredAccountCards is already filtered by PAL code in the useMemo (lines 60-116)
            // This is the same logic as CardSelectionModal's handleSelectAll with filteredCards
            const cardsToSelect = [...filteredAccountCards];
            setSelectedCards(cardsToSelect);
            setIsMultiCardReport(true);
            // Update effective mode to 'multiple' since we're now selecting multiple cards
            effectiveCardSelectionMode = 'multiple';
            setCardSelectionMode('multiple');
            shouldAutoSelectCards = true;
            // Mark that we triggered auto-selection so useEffect can handle the fetch
            autoSelectTriggeredRef.current = true;
        }
        
        // Handle card selection without applying (use effective mode)
        handleSelection('card', effectiveCardSelectionMode, false);
        
        // Handle PAL code selection without applying
        handleSelection('palCode', palCodeSelectionMode, false);
        
        // Only call onApplySelection immediately if we didn't auto-select cards
        // If we auto-selected, the useEffect will handle the fetch after state updates
        if (!shouldAutoSelectCards) {
            // Apply both selections together
            onApplySelection();
        }
    };

    // useEffect to handle fetch after auto-selecting cards
    // This ensures state updates are committed before fetching
    useEffect(() => {
        if (autoSelectTriggeredRef.current && selectedCards && selectedCards.length > 0 && cardSelectionMode === 'multiple') {
            // Reset the flag
            autoSelectTriggeredRef.current = false;
            // Trigger the fetch now that state is updated
            onApplySelection();
        }
    }, [selectedCards, cardSelectionMode, onApplySelection]);

    // Auto-update selectedCards when range values change
    useEffect(() => {
        if (cardSelectionMode === 'range' && fromCard && toCard) {
            const fromIndex = filteredAccountCards.findIndex(card => card.cardId === fromCard.cardId);
            const toIndex = filteredAccountCards.findIndex(card => card.cardId === toCard.cardId);
            
            if (fromIndex !== -1 && toIndex !== -1) {
                const start = Math.min(fromIndex, toIndex);
                const end = Math.max(fromIndex, toIndex);
                const rangeCards = filteredAccountCards.slice(start, end + 1);
                
                // Only update if the selection has actually changed
                const currentCardIds = selectedCards.map(card => card.cardId).sort();
                const newCardIds = rangeCards.map(card => card.cardId).sort();
                
                if (JSON.stringify(currentCardIds) !== JSON.stringify(newCardIds)) {
                    setSelectedCards(rangeCards);
                    setIsMultiCardReport(true);
                }
            }
        }
    }, [fromCard, toCard, filteredAccountCards, cardSelectionMode]);

    // Handle card selection modal
    const handleOpenCardSelectionModal = () => {
        setShowCardSelectionModal(true);
    };

    const handleCardSelectionConfirm = (selectedCardsFromModal) => {
        setSelectedCards(selectedCardsFromModal);
        setShowCardSelectionModal(false);
    };

    // Auto-update selectedPalCode when PAL code range values change
    useEffect(() => {
        if (palCodeSelectionMode === 'range' && fromPalCode && toPalCode) {
            const fromIndex = sortedPalCodeOptions.findIndex(pal => pal.value === fromPalCode.value);
            const toIndex = sortedPalCodeOptions.findIndex(pal => pal.value === toPalCode.value);
            
            if (fromIndex !== -1 && toIndex !== -1) {
                const start = Math.min(fromIndex, toIndex);
                const end = Math.max(fromIndex, toIndex);
                const rangePalCodes = sortedPalCodeOptions.slice(start, end + 1);
                
                // Only update if the selection has actually changed
                const currentPalCodeValues = selectedPalCode.map(pal => pal.value).sort();
                const newPalCodeValues = rangePalCodes.map(pal => pal.value).sort();
                
                if (JSON.stringify(currentPalCodeValues) !== JSON.stringify(newPalCodeValues)) {
                    setSelectedPalCode(rangePalCodes);
                }
            }
        }
    }, [fromPalCode, toPalCode, sortedPalCodeOptions, palCodeSelectionMode]);

    // Reset card selection when PAL code selection changes
    useEffect(() => {
        // Create a key to track PAL code selection changes
        const palCodeKey = palCodeSelectionMode === 'single' 
            ? (selectedPalCode && selectedPalCode.length > 0 ? selectedPalCode.map(p => p.value).sort().join(',') : '')
            : palCodeSelectionMode === 'range'
            ? (fromPalCode && toPalCode ? `${fromPalCode.value}-${toPalCode.value}` : '')
            : palCodeSelectionMode === 'type'
            ? (selectedPalCodeType?.value || '')
            : '';
        
        // Check if PAL code selection has changed (and it's not the initial empty state)
        if (prevPalCodeKeyRef.current !== '' && prevPalCodeKeyRef.current !== palCodeKey && palCodeKey !== '') {
            // PAL code selection changed - reset card selection and mode to 'single'
            setSelectedCards([]);
            setAccountCardId(null);
            setAccountCardName(null);
            setFromCard(null);
            setToCard(null);
            setIsMultiCardReport(false);
            // Reset card selection mode to 'single' when PAL code changes
            setCardSelectionMode('single');
        }
        
        // Update the ref with current PAL code key
        if (palCodeKey !== '') {
            prevPalCodeKeyRef.current = palCodeKey;
        }
    }, [selectedPalCode, fromPalCode, toPalCode, palCodeSelectionMode, selectedPalCodeType, setSelectedCards, setAccountCardId, setAccountCardName, setFromCard, setToCard, setIsMultiCardReport, setCardSelectionMode]);

    // Remove auto-fetch - only fetch when Apply button is clicked

    return (
        <>
        <div className={`filter-bar-container unified ${direction}`}>
            {/* Unified Filter Section */}
            <div className="unified-filter-section">
                {/* Row 1: search + dates + currency, checkboxes at end */}
                <div className="filter-content filter-content-toolbar">
                    <div className="filter-group filter-search-group">
                        <TextField
                            placeholder={t('SearchTransactions') || 'Search Transactions'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                            variant="outlined"
                            size="small"
                            InputProps={{
                                ...(direction === 'ltr' ? {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                } : {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                })
                            }}
                            inputProps={{
                                style: {
                                    textAlign: direction === 'rtl' ? 'right' : 'left',
                                    direction: direction === 'rtl' ? 'rtl' : 'ltr'
                                },
                            }}
                        />
                    </div>

                    <div className={`document-filter-row ${direction}`} dir={direction}>
                        <Box className="document-data-view">
                            <ToggleButtonGroup
                                value={isMonthly ? 'monthly' : 'yearly'}
                                exclusive
                                onChange={(event, newValue) => {
                                    if (newValue !== null) {
                                        handleRangeChange(newValue === 'monthly');
                                    }
                                }}
                                size="small"
                            >
                                <ToggleButton value="yearly" aria-label="yearly">
                                    {t('Yearly')}
                                </ToggleButton>
                                <ToggleButton value="monthly" aria-label="monthly">
                                    {t('Monthly')}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {typeof setRowViewMode === 'function' && (
                            <Box className="document-data-view">
                                <Tooltip title={t('SummaryViewTooltip')} arrow placement="top">
                                    <ToggleButtonGroup
                                        value={rowViewMode}
                                        exclusive
                                        onChange={(event, newValue) => {
                                            if (newValue !== null) setRowViewMode(newValue);
                                        }}
                                        size="small"
                                    >
                                        <ToggleButton value="full" aria-label="full">
                                            {t('FullView')}
                                        </ToggleButton>
                                        <ToggleButton value="summary" aria-label="summary">
                                            {t('SummaryView')}
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Tooltip>
                            </Box>
                        )}

                        {isMonthly ? (
                            filterByDocumentDate ? (
                                <div className={headerStyles.dateSelectRange}>
                                    <div className={headerStyles.dateSelect} style={{ width: '180px', minWidth: 0 }}>
                                        <CustomDatePicker
                                            date={startDate}
                                            onDateChange={handleStartDateChange}
                                            label={t('StartDate')}
                                            height="40px"
                                        />
                                    </div>
                                    <div className={headerStyles.rangeDivider} />
                                    <div className={headerStyles.dateSelect} style={{ width: '180px', minWidth: 0 }}>
                                        <CustomDatePicker
                                            date={endDate}
                                            onDateChange={handleEndDateChange}
                                            label={t('EndDate')}
                                            height="40px"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className={headerStyles.dateSelectRange}>
                                    <div className={headerStyles.dateSelect}>
                                        <FilterMonthYearPicker
                                            year={startDate}
                                            month={startDate}
                                            onYearChange={(newYear) => handleStartDateChangeNew(newYear, 'year')}
                                            onMonthChange={(newMonth) => handleStartDateChangeNew(newMonth, 'month')}
                                            labelYear={t('StartYear')}
                                            labelMonth={t('StartMonth')}
                                            direction={direction}
                                        />
                                    </div>
                                    <div className={headerStyles.rangeDivider} />
                                    <div className={headerStyles.dateSelect}>
                                        <FilterMonthYearPicker
                                            year={endDate}
                                            month={endDate}
                                            onYearChange={(newYear) => handleEndDateChangeNew(newYear, 'year')}
                                            onMonthChange={(newMonth) => handleEndDateChangeNew(newMonth, 'month')}
                                            labelYear={t('EndYear')}
                                            labelMonth={t('EndMonth')}
                                            direction={direction}
                                        />
                                    </div>
                                </div>
                            )
                        ) : (
                            <div className={`${headerStyles.dateSelectRange} ${headerStyles.dateSelectRangeCompact}`}>
                                <div className={headerStyles.dateSelectYear}>
                                    <FilterMonthYearPicker
                                        year={startDate}
                                        onYearChange={(date) => handleStartDateChange(date)}
                                        labelYear={t('StartDate')}
                                        direction={direction}
                                        yearOnly
                                    />
                                </div>
                                <div className={headerStyles.rangeDivider} />
                                <div className={headerStyles.dateSelectYear}>
                                    <FilterMonthYearPicker
                                        year={endDate}
                                        onYearChange={(date) => handleEndDateChange(date)}
                                        labelYear={t('EndDate')}
                                        direction={direction}
                                        yearOnly
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {typeof setSelectedCurrency === 'function' && availableCurrencies && availableCurrencies.length > 0 && (
                        <div className="filter-group" style={{ maxWidth: '180px', alignItems: 'center', alignSelf: 'center' }}>
                            <Tooltip
                                title={selectedCurrencyOption ? selectedCurrencyOption.label : t('Currency')}
                                arrow
                                placement="top"
                            >
                                <div>
                                    <AutocompleteSelectFilter
                                        options={currencyOptions}
                                        selectedValues={selectedCurrencyOption}
                                        onChange={(event, newValue) => {
                                            if (!newValue || newValue.value === 'SHEKEL') {
                                                setSelectedCurrency(null);
                                            } else if (newValue.value === 'MIXED') {
                                                setSelectedCurrency('MIXED');
                                            } else {
                                                setSelectedCurrency(Number(newValue.value));
                                            }
                                        }}
                                        label={t('Currency')}
                                        placeholder={t('Currency')}
                                        isMultiple={false}
                                        direction={direction}
                                        portalMenu
                                        searchable
                                    />
                                </div>
                            </Tooltip>
                        </div>
                    )}

                    <Box className="document-mode-toggles filter-checkboxes-end" dir={direction}>
                        <Box className={`document-mode-toggle${!processedFilesCheckbox ? ' is-checked' : ''}`}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={!processedFilesCheckbox}
                                        onChange={() => handleProcessFilesCheckboxClick(!processedFilesCheckbox)}
                                        inputProps={{ 'aria-label': t('ShowUnprocessed') }}
                                    />
                                }
                                label={t('ShowUnprocessed')}
                            />
                        </Box>
                        <Box className={`document-mode-toggle${showZeros ? ' is-checked' : ''}`}>
                            <Tooltip title={showZeros ? t('HideZeros') : t('ShowZeros')}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={showZeros}
                                            onChange={(e) => setShowZeros(e.target.checked)}
                                            inputProps={{ 'aria-label': showZeros ? t('ShowZeros') : t('HideZeros') }}
                                        />
                                    }
                                    label={showZeros ? t('ShowZeros') : t('HideZeros')}
                                    sx={{ m: 0 }}
                                />
                            </Tooltip>
                        </Box>
                        {typeof setFilterByDocumentDate === 'function' && (
                            <>
                                <Box className={`document-mode-toggle${!filterByDocumentDate ? ' is-checked' : ''}`}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={!filterByDocumentDate}
                                                onChange={() => setFilterByDocumentDate(false)}
                                                inputProps={{ 'aria-label': t('FilterByRegisterMonthDate') }}
                                            />
                                        }
                                        label={t('FilterByRegisterMonthDate')}
                                        sx={{ m: 0 }}
                                    />
                                </Box>
                                <Box className={`document-mode-toggle${filterByDocumentDate ? ' is-checked' : ''}`}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={!!filterByDocumentDate}
                                                onChange={() => setFilterByDocumentDate(true)}
                                                inputProps={{ 'aria-label': t('FilterByDocumentDate') }}
                                            />
                                        }
                                        label={t('FilterByDocumentDate')}
                                        sx={{ m: 0 }}
                                    />
                                </Box>
                            </>
                        )}
                    </Box>
                </div>
                {/* Row 2: pal/card filters + Apply */}
                <div className="filter-content filter-content-main">
                    <div className="filter-row filter-row-single">
                        {cardSelectionMode !== 'all' && (
                            <div className={`filter-group ${palCodeSelectionMode === 'single' ? 'filter-group-single' : ''}`}>
                                <label className="filter-label">
                                    <FilterListIcon sx={{ fontSize: 14, color: '#304fff' }} />
                                    {t('PalCodeFilter')}
                                </label>
                            <div className="palcode-controls">
                                <FormControl size="small" className="mode-select">
                                    <InputLabel sx={{ fontSize: '11px' }}>{t('Mode')}</InputLabel>
                                    <Select
                                        value={palCodeSelectionMode}
                                        onChange={(e) => {
                                            const newMode = e.target.value;
                                            setPalCodeSelectionMode(newMode);
                                            
                                            // Clear PAL code selections when switching modes
                                            if (newMode === 'single') {
                                                setFromPalCode(null);
                                                setToPalCode(null);
                                                setSelectedPalCode([]);
                                                setSelectedPalCodeType(null);
                                            } else if (newMode === 'range') {
                                                setSelectedPalCode([]);
                                                setFromPalCode(null);
                                                setToPalCode(null);
                                                setSelectedPalCodeType(null);
                                            } else if (newMode === 'type') {
                                                setSelectedPalCode([]);
                                                setFromPalCode(null);
                                                setToPalCode(null);
                                            }
                                        }}
                                        label={t('Mode')}
                                        MenuProps={getFilterModeMenuProps(direction)}
                                        sx={{
                                            height: '40px',
                                            fontSize: '12px',
                                            direction,
                                            '& .MuiSelect-select': {
                                                padding: '8px 12px',
                                                fontSize: '12px',
                                                textAlign: 'center',
                                            }
                                        }}
                                    >
                                        <MenuItem value="single" sx={{ fontSize: '12px' }}>{t('Single')}</MenuItem>
                                        <MenuItem value="range" sx={{ fontSize: '12px' }}>{t('Range')}</MenuItem>
                                        <MenuItem value="type" sx={{ fontSize: '12px' }}>{t('PalCodeType')}</MenuItem>
                                    </Select>
                                </FormControl>
                                
                                {palCodeSelectionMode === 'single' && (
                                    <Tooltip 
                                        title={selectedPalCode && selectedPalCode.length > 0 ? selectedPalCode[0].label : t('SelectPalCode')}
                                        arrow
                                        placement="top"
                                    >
                                        <div>
                                            <div style={{ width: 200 }}>
                                                <AutocompleteSelectFilter
                                                    options={sortedPalCodeOptions || []}
                                                    groupBy={groupPalCodeBy}
                                                    selectedValues={selectedPalCode && selectedPalCode.length > 0 ? selectedPalCode[0] : null}
                                                    onChange={(event, newValue) => {
                                                        setSelectedPalCode(newValue ? [newValue] : []);
                                                    }}
                                                    label={t('SelectPalCode')}
                                                    placeholder={t('SelectPalCode')}
                                                    isMultiple={false}
                                                    direction={direction}
                                                    portalMenu
                                                    searchable
                                                />
                                            </div>
                                        </div>
                                    </Tooltip>
                                )}
                                
                                {palCodeSelectionMode === 'type' && (
                                    <Tooltip
                                        title={selectedPalCodeType ? selectedPalCodeType.label : t('PalCodeType')}
                                        arrow
                                        placement="top"
                                    >
                                        <div>
                                            <div style={{ width: 200 }}>
                                                <AutocompleteSelectFilter
                                                    options={palCodeTypeOptions}
                                                    selectedValues={selectedPalCodeType}
                                                    onChange={(event, newValue) => {
                                                        setSelectedPalCodeType(newValue);
                                                    }}
                                                    label={t('PalCodeType')}
                                                    placeholder={t('PalCodeType')}
                                                    isMultiple={false}
                                                    direction={direction}
                                                    portalMenu
                                                    searchable
                                                />
                                            </div>
                                        </div>
                                    </Tooltip>
                                )}
                                
                                {palCodeSelectionMode === 'range' && (
                                    <div className="range-controls-vertical">
                                        <Tooltip 
                                            title={fromPalCode ? fromPalCode.label : t('From')}
                                            arrow
                                            placement="top"
                                        >
                                            <div>
                                                <div style={{ width: 160 }}>
                                                    <AutocompleteSelectFilter
                                                        options={sortedPalCodeOptions || []}
                                                        groupBy={groupPalCodeBy}
                                                        selectedValues={fromPalCode}
                                                        onChange={(event, newValue) => {
                                                            setFromPalCode(newValue);
                                                        }}
                                                        label={t('From') || 'From'}
                                                        placeholder={t('From') || 'From'}
                                                        isMultiple={false}
                                                        direction={direction}
                                                        portalMenu
                                                        searchable
                                                    />
                                                </div>
                                            </div>
                                        </Tooltip>
                                        
                                        <Tooltip 
                                            title={toPalCode ? toPalCode.label : t('To')}
                                            arrow
                                            placement="top"
                                        >
                                            <div>
                                                <div style={{ width: 160 }}>
                                                    <AutocompleteSelectFilter
                                                        options={sortedPalCodeOptions || []}
                                                        groupBy={groupPalCodeBy}
                                                        selectedValues={toPalCode}
                                                        onChange={(event, newValue) => {
                                                            setToPalCode(newValue);
                                                        }}
                                                        label={t('To') || 'To'}
                                                        placeholder={t('To') || 'To'}
                                                        isMultiple={false}
                                                        direction={direction}
                                                        portalMenu
                                                        searchable
                                                    />
                                                </div>
                                            </div>
                                        </Tooltip>
                                    </div>
                                )}
                            </div>
                        </div>
                        )}
                        <div className="filter-group">
                            <label className="filter-label">
                                <AccountBalanceIcon sx={{ fontSize: 12, color: '#304fff' }} />
                                {t('CardSelection')}
                            </label>
                            <div className="card-controls">
                                <FormControl size="small" className="mode-select">
                                    <InputLabel sx={{ fontSize: '11px' }}>{t('Mode')}</InputLabel>
                                    <Select
                                        value={cardSelectionMode}
                                        onChange={(e) => {
                                            const newMode = e.target.value;
                                            setCardSelectionMode(newMode);
                                            
                                            // Clear selections when switching modes to ensure clean state
                                            if (newMode === 'single') {
                                                setSelectedCards([]);
                                                setFromCard(null);
                                                setToCard(null);
                                                setIsMultiCardReport(false);
                                            } else if (newMode === 'range') {
                                                setAccountCardId(null);
                                                setAccountCardName(null);
                                                setSelectedCards([]);
                                                setIsMultiCardReport(false);
                                            } else if (newMode === 'multiple') {
                                                setAccountCardId(null);
                                                setAccountCardName(null);
                                                setFromCard(null);
                                                setToCard(null);
                                                setIsMultiCardReport(false);
                                            } else if (newMode === 'all') {
                                                setAccountCardId(null);
                                                setAccountCardName(null);
                                                setFromCard(null);
                                                setToCard(null);
                                                setSelectedCards([]);
                                                setIsMultiCardReport(false);
                                                
                                                // Clear PAL code selection when selecting 'all' cards
                                                setSelectedPalCode([]);
                                                setFromPalCode(null);
                                                setToPalCode(null);
                                            }
                                        }}
                                        label={t('Mode')}
                                        MenuProps={getFilterModeMenuProps(direction)}
                                        sx={{
                                            height: '40px',
                                            fontSize: '12px',
                                            direction,
                                            '& .MuiSelect-select': {
                                                padding: '8px 12px',
                                                fontSize: '12px',
                                                textAlign: 'center',
                                            }
                                        }}
                                    >
                                        <MenuItem value="single" sx={{ fontSize: '12px' }}>{t('Single')}</MenuItem>
                                        <MenuItem value="range" sx={{ fontSize: '12px' }}>{t('Range')}</MenuItem>
                                        <MenuItem value="multiple" sx={{ fontSize: '12px' }}>{t('Multiple')}</MenuItem>
                                        <MenuItem value="all" sx={{ fontSize: '12px' }}>{t('All')}</MenuItem>
                                    </Select>
                                </FormControl>
                                
                                {cardSelectionMode === 'single' && (
                                    <Tooltip 
                                        title={
                                            filteredAccountCards.find((option) => option.cardId === accountCardId) 
                                                ? `${accountCardId} - ${accountCardName}`
                                                : t('SelectCard') || 'Select Card'
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <div>
                                            <div style={{ width: 200 }}>
                                                <AutocompleteSelectFilter
                                                    options={accountCardAutocompleteOptions}
                                                    groupBy={groupAccountCardBy}
                                                    selectedValues={
                                                        accountCardAutocompleteOptions.find((option) => option.value === accountCardId) || null
                                                    }
                                                    onChange={(event, newValue) => {
                                                        if (newValue) {
                                                            const selectedCard = newValue.card || filteredAccountCards.find(card => card.cardId === newValue.value);
                                                            setAccountCardId(newValue.value);
                                                            setAccountCardName(selectedCard?.name || '');
                                                        } else {
                                                            setAccountCardId(null);
                                                            setAccountCardName(null);
                                                        }
                                                    }}
                                                    label={t('SelectCard') || 'Select Card'}
                                                    placeholder={t('SelectCard') || 'Select Card'}
                                                    isMultiple={false}
                                                    direction={direction}
                                                    portalMenu
                                                    searchable
                                                />
                                            </div>
                                        </div>
                                    </Tooltip>
                                )}
                                
                                {cardSelectionMode === 'range' && (
                                    <div className="range-controls-vertical">
                                        <Tooltip 
                                            title={fromCard ? `${fromCard.cardId} - ${fromCard.name}` : t('From')}
                                            arrow
                                            placement="top"
                                        >
                                            <div>
                                                <div style={{ width: 160 }}>
                                                    <AutocompleteSelectFilter
                                                        options={accountCardAutocompleteOptions}
                                                        groupBy={groupAccountCardBy}
                                                        selectedValues={
                                                            fromCard
                                                                ? accountCardAutocompleteOptions.find((option) => option.value === fromCard.cardId) || null
                                                                : null
                                                        }
                                                        onChange={(event, newValue) => {
                                                            if (newValue) {
                                                                const selectedCard = newValue.card || filteredAccountCards.find(card => card.cardId === newValue.value);
                                                                setFromCard(selectedCard);
                                                            } else {
                                                                setFromCard(null);
                                                            }
                                                        }}
                                                        label={t('From') || 'From'}
                                                        placeholder={t('From') || 'From'}
                                                        isMultiple={false}
                                                        direction={direction}
                                                        portalMenu
                                                        searchable
                                                    />
                                                </div>
                                            </div>
                                        </Tooltip>
                                        
                                        <Tooltip 
                                            title={toCard ? `${toCard.cardId} - ${toCard.name}` : t('To')}
                                            arrow
                                            placement="top"
                                        >
                                            <div>
                                                <div style={{ width: 160 }}>
                                                    <AutocompleteSelectFilter
                                                        options={accountCardAutocompleteOptions}
                                                        groupBy={groupAccountCardBy}
                                                        selectedValues={
                                                            toCard
                                                                ? accountCardAutocompleteOptions.find((option) => option.value === toCard.cardId) || null
                                                                : null
                                                        }
                                                        onChange={(event, newValue) => {
                                                            if (newValue) {
                                                                const selectedCard = newValue.card || filteredAccountCards.find(card => card.cardId === newValue.value);
                                                                setToCard(selectedCard);
                                                            } else {
                                                                setToCard(null);
                                                            }
                                                        }}
                                                        label={t('To') || 'To'}
                                                        placeholder={t('To') || 'To'}
                                                        isMultiple={false}
                                                        direction={direction}
                                                        portalMenu
                                                        searchable
                                                    />
                                                </div>
                                            </div>
                                        </Tooltip>
                                    </div>
                                )}
                                
                                {cardSelectionMode === 'multiple' && (
                                    <Tooltip 
                                        title={
                                            selectedCards.length > 0 
                                                ? selectedCards.map(card => `${card.cardId} - ${card.name}`).join(', ')
                                                : t('SelectCards')
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <Button
                                            variant="outlined"
                                            onClick={handleOpenCardSelectionModal}
                                            sx={{ 
                                                width: "200px", 
                                                height: "40px",
                                                fontSize: "12px",
                                                textTransform: "none",
                                                justifyContent: "flex-start",
                                                borderRadius: "12px",
                                                border: "2px solid rgba(48, 79, 255, 0.15)",
                                                color: "#304fff",
                                                fontWeight: 600,
                                                '&:hover': {
                                                    borderColor: "rgba(48, 79, 255, 0.3)",
                                                    backgroundColor: "rgba(48, 79, 255, 0.05)"
                                                }
                                            }}
                                        >
                                            {selectedCards.length > 0 
                                                ? `${selectedCards.length} ${t('Cards')}` 
                                                : t('SelectCards')
                                            }
                                        </Button>
                                    </Tooltip>
                                )}

                            </div>
                            
                        </div>
                        <div dir={direction} className="filter-group filter-group-apply">
                        <button 
                            onClick={handleCardSelection}
                            className="filter-apply-btn"
                            style={{
                                background: '#304fff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '2px 14px',
                                height: '36px',
                                minHeight: '36px',
                                maxHeight: '36px',
                                boxSizing: 'border-box',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '14px',
                                whiteSpace: 'nowrap',
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: '0 6px 20px rgba(48, 79, 255, 0.3)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                minWidth: '100px',
                                flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
                            }}
    
                        >
                            <PlayArrowIcon sx={{ fontSize: 16, width:'20px' ,backgroundColor:'#304fff'}} />
                            {t('Apply')}
                        </button>
                    </div>
                    </div>
                </div>

            </div>
        </div>
        
        <CardSelectionModal
            open={showCardSelectionModal}
            onClose={() => setShowCardSelectionModal(false)}
            onConfirm={handleCardSelectionConfirm}
            availableCards={filteredAccountCards}
            selectedCards={selectedCards}
            title="SelectCards"
            direction={direction}
        />
        </>
    );
};

export default FilterBar;
